"use client";

import { TypePageSkeleton } from "@contentful/types";
import { H1 } from "@yardzen/ui/components/h1";
import { H2 } from "@yardzen/ui/components/h2";
import { P } from "@yardzen/ui/components/p";
import { Page } from "@yardzen/ui/components/page";
import { Entry } from "contentful/dist/types/types";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/**
 * Guides user to choosing the preview parameters and displays a list of all pages.
 * ?changed=yes or ?on=timestamp
 */
export function Preview({
  pageList,
}: {
  pageList: Entry<TypePageSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[];
}) {
  const [changed, setChanged] = useState<string>("false");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [timeZone, setTimeZone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const timezones = [
    { label: "Eastern Time", value: "America/New_York" },
    { label: "Pacific Time", value: "America/Los_Angeles" },
    { label: "UTC", value: "UTC" },
  ];
  // Avoid Hydration error with startDate initializing to new Date
  useEffect(() => {
    setStartDate(new Date());
  }, []);

  // I don't want to convert the time to the timezone. Use formatInTimeZone just to get the utc offset -07:00
  const dateFormatted = startDate
    ? `${format(startDate, "yyyy-MM-dd'T'HH:mm:ss")}${formatInTimeZone(startDate, timeZone, "XXX")}`
    : "";
  const param = `?${changed === "true" ? "changed=yes" : `on=${dateFormatted}`}`;
  return (
    <Page className="px-0 pt-16 md:pt-20" bg="beige-lighter">
      <div className="mx-auto w-fit ">
        <H1 className="mb-2">Preview</H1>
        <div>
          <label className="mr-3">
            <input
              type="radio"
              name="changed"
              className="mr-1"
              value="false"
              checked={changed === "false"}
              onChange={(e) => setChanged(e.target.value)}
            />
            To view newly changed content and content scheduled to
            publish/unpublish in the future
          </label>
        </div>
        <div>
          <label className="mr-3">
            <input
              type="radio"
              name="changed"
              className="mr-1"
              value="true"
              checked={changed === "true"}
              onChange={(e) => setChanged(e.target.value)}
            />
            To view newly changed content that is already published
          </label>
        </div>
        {changed === "false" && (
          <div className="mt-2">
            <H2>Pick Date</H2>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              showTimeSelect
              dateFormat="Pp"
            />
            <H2 className="mt-2">TimeZone</H2>
            <div>
              {timezones.map((tz) => (
                <label key={tz.value} className="mr-3">
                  <input
                    type="radio"
                    name="timezone"
                    className="mr-1"
                    value={tz.value}
                    checked={timeZone === tz.value}
                    onChange={(e) => setTimeZone(e.target.value)}
                  />
                  {tz.label}
                </label>
              ))}
            </div>
          </div>
        )}
        <H2 className="mt-2">Url parameter</H2>
        <P noSpacing className="mb-2">
          {param}
        </P>
        <H2>Pages</H2>
        <ul>
          {pageList &&
            pageList
              .sort((a, b) => a?.fields?.slug.localeCompare(b.fields?.slug))
              .map((page) => (
                <li key={page.sys.id} className="my-1">
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${page.fields.slug}${param}`}
                    className="hover:underline"
                  >
                    {page.fields.slug}
                  </a>
                </li>
              ))}
        </ul>
      </div>
    </Page>
  );
}
